import React, { useEffect, useState } from "react";
import { getMainWidth, handelTitle } from '../../utils';
import Breadcrumb from '../../components/Breadcrumb';
import { useLocation } from '@tanstack/react-router';

import CardImg from './CardImg';
import SectionCardUp from './SectionCardUp';
import SectionCardDown from './SectionCardDown';
import './ItemDetails.css';
import md5 from "md5";
import Comments from "../../components/Comments/index.jsx";
import { useItemDetails } from "./useItemDetails";

/**
 * Item Details page container.
 *
 * Displays detailed information about a specific NFT item including:
 * - Item image with navigation (next/previous)
 * - Price and auction information
 * - Place bid functionality
 * - Activity history and bids
 * - Comments section
 *
 * Uses the useItemDetails custom hook for data fetching and state management.
 * Handles loading states and error display.
 *
 * @module containers/ItemDetails
 * @category Containers
 * @param {Object} props
 * @param {string} props.chain - The blockchain chain identifier (from route)
 * @param {string} props.contract - The collection contract address or ID
 * @param {string} props.tokenId - The token ID of the NFT
 * @param {Object} props.initialCollectionInfo - Optional preloaded collection info from route loader
 * @param {Object} props.initialTokenData - Optional preloaded NFT metadata from route loader
 * @returns {React.ReactElement} Rendered item details page
 */
const ItemDetailsContainer = ({ chain, contract, tokenId, initialCollectionInfo, initialTokenData }) => {
    const location = useLocation();

    // Extract tokenId and contractId from URL pathname for consistency (used by CardImg for nav links)
    const pathParts = location.pathname.split('/');
    const getIdsFromPath = () => {
        if (pathParts.length >= 4) {
            return {
                tokenId: pathParts[pathParts.length - 1],
                contractId: pathParts[pathParts.length - 2]
            };
        }
        return { tokenId: null, contractId: null };
    };

    const { tokenId: initialTokenId, contractId: initialContractId } = getIdsFromPath();

    const [itemId, setItemId] = useState(initialTokenId);
    const [contractId, setContractId] = useState(initialContractId);

    // All the complex logic is now handled by our custom hook
    const { collectionInfo, tokenInfo, isLoading, error, refetch } = useItemDetails(
        contractId,
        itemId,
        {
            initialCollectionInfo,
            initialTokenData
        }
    );

    useEffect(() => {
        handelTitle('Item Details');
        getMainWidth();
    }, []);

    // Sync state with URL changes (component reuse)
    useEffect(() => {
        const { tokenId: newTokenId, contractId: newContractId } = getIdsFromPath();

        setItemId(prev => prev !== newTokenId ? newTokenId : prev);
        setContractId(prev => prev !== newContractId ? newContractId : prev);
    }, [location.pathname]);

    if (error) {
        return (
            <>
                <div className="container-fluid">
                     <div className="page-header breadcrumb-header min-height-40 border-radius-xl mt-4 mb-30 ItemDetailsIMG">
                        <Breadcrumb text1='Item Details' text2='Error'/>
                    </div>
                    <div className="row">
                        <div className="col-lg-7">
                            <h3>We looked everywhere, but we couldn't find your item!</h3>
                            <p>{error}</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <main className="main-content mt-1 border-radius-lg">
                <Breadcrumb text1='Item Details' text2={collectionInfo?.symbol || ''}/>

                <div style={{display: "flex", justifyContent: "center", maxWidth: "100%"}} >
                    <div className="profileFlex vh-100" style={{display: "flex", justifyContent: "center", maxWidth: "1400px", flexBasis: "100%"}}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: "39%",
                            }}
                            className="m-0 profileFlex">

                            <CardImg
                                className="ms-1 me-1 card-purple p-2 card-img-container"
                                img={tokenInfo?.img}
                                cpos={itemId}
                                npos={parseInt(itemId) + 1}
                                ppos={parseInt(itemId) > 1 ? parseInt(itemId) - 1 : null}
                                setItem={setItemId}
                                tokInfo={tokenInfo}
                            />
                            <div className="mt-2">
                                <Comments
                                    noDisplayMedia={true}
                                    itemHash={md5(location.pathname.toString())}
                                    metaAction={tokenInfo?.metadata?.name ? `Commented on ${tokenInfo.metadata.name}` : `Commented on ${collectionInfo?.symbol} #${tokenInfo?.tid}`}
                                    actionLocation={location.pathname}
                                    actionMedia={{url: tokenInfo?.img, name: `${collectionInfo?.symbol} #${tokenInfo?.tid}`, mime: "image/jpg"}}
                                />
                            </div>
                        </div>
                        <div
                            style={{flex: 2, display: "flex", flexDirection: "column", minWidth: "58%", minHeight: "100%"}}
                            className="col1-margins col2-margins">
                            <SectionCardUp 
                                shit={collectionInfo} 
                                itemid={itemId} 
                                ti={tokenInfo} 
                                rr={refetch}
                            />
                            <div className="row mb-2">
                                <SectionCardDown 
                                    data1={collectionInfo} 
                                    data2={collectionInfo} 
                                    ti={tokenInfo} 
                                    rr={refetch}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}

export default ItemDetailsContainer;
